import axios from 'axios'
import { isAxiosError } from '@traba/utils'
import { getAuth } from 'firebase/auth'
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  MessageFeedback,
  SubmitFeedbackRequest,
} from '@traba/neutron-chat'

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry_auth?: boolean
  }
}

export function getStreamBaseUrl(): string {
  return import.meta.env.VITE_NEO_SERVER_URL ?? ''
}

export async function getFirebaseIdToken(
  forceRefresh = false,
): Promise<string | null> {
  const auth = getAuth()
  const token = await auth.currentUser?.getIdToken(forceRefresh)
  return token ?? null
}

// The active workspace the user is acting in. Held at module scope so the
// request interceptor can stamp it on every Neo call without threading it
// through each caller. Set by ActiveOrgProvider; null means "personal", which
// the backend also treats as the default.
let activeOrgId: string | null = null

export function setNeoActiveOrgId(orgId: string | null): void {
  activeOrgId = orgId
}

export function getNeoActiveOrgId(): string | null {
  return activeOrgId
}

export const neoStreamApi = axios.create()

neoStreamApi.interceptors.request.use(async (config) => {
  if (!config.baseURL) {
    config.baseURL = getStreamBaseUrl()
  }

  const idToken = await getFirebaseIdToken()
  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`
  }

  if (activeOrgId) {
    config.headers['X-Neo-Org-Id'] = activeOrgId
  }

  return config
})

// Retry once on 401 with a force-refreshed token before giving up.
neoStreamApi.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (
      isAxiosError(error) &&
      error.response?.status === 401 &&
      error.config &&
      !error.config._retry_auth
    ) {
      const freshToken = await getFirebaseIdToken(true)
      if (freshToken && error.config.headers) {
        error.config.headers['Authorization'] = `Bearer ${freshToken}`
        error.config._retry_auth = true
        return neoStreamApi(error.config)
      }
    }
    throw error
  },
)

export type NeoAuthStatus = 'idle' | 'checking' | 'ready' | 'error'
export type NeoAuthError =
  | 'no-user'
  | 'missing-claims'
  | 'token-refresh-failed'
  | 'server-rejected'
  | 'unknown'

export function useNeoAuth(isOpen: boolean = true) {
  const [status, setStatus] = useState<NeoAuthStatus>('idle')
  const [errorDetail, setErrorDetail] = useState<NeoAuthError | null>(null)
  const checkingRef = useRef(false)
  const checkSeqRef = useRef(0)

  const checkAuth = useCallback(async () => {
    if (checkingRef.current) {
      return
    }
    checkingRef.current = true
    const seq = ++checkSeqRef.current
    setStatus('checking')
    setErrorDetail(null)

    const isStale = () => seq !== checkSeqRef.current

    try {
      const auth = getAuth()
      if (!auth.currentUser) {
        if (isStale()) {
          return
        }
        setStatus('error')
        setErrorDetail('no-user')
        return
      }

      const token = await auth.currentUser.getIdToken()
      if (isStale()) {
        return
      }
      if (!token) {
        setStatus('error')
        setErrorDetail('token-refresh-failed')
        return
      }

      // Lightweight ping to verify the token works against the server.
      const baseUrl = getStreamBaseUrl()
      if (!baseUrl) {
        setStatus('ready')
        return
      }

      const activeOrgId = getNeoActiveOrgId()
      const response = await fetch(`${baseUrl}/neo/chat/sessions?limit=1`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrgId ? { 'X-Neo-Org-Id': activeOrgId } : {}),
        },
      })

      if (isStale()) {
        return
      }

      if (response.ok) {
        setStatus('ready')
        return
      }

      if (response.status === 401 || response.status === 403) {
        setStatus('error')
        setErrorDetail('server-rejected')
        return
      }

      // Non-auth server errors: let the user try anyway.
      setStatus('ready')
    } catch (err: unknown) {
      if (isStale()) {
        return
      }
      console.error('[Neo] auth pre-flight failed', {
        baseUrl: getStreamBaseUrl(),
        err,
      })
      setStatus('error')
      setErrorDetail('token-refresh-failed')
    } finally {
      checkingRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isOpen && status === 'idle') {
      checkAuth()
    }
    if (!isOpen) {
      // Stale-out any in-flight checkAuth so it can't set state after close.
      checkSeqRef.current++
      setStatus('idle')
      setErrorDetail(null)
    }
  }, [isOpen, status, checkAuth])

  return { status, errorDetail, retry: checkAuth }
}

export async function submitMessageFeedback(
  messageId: string,
  data: SubmitFeedbackRequest,
): Promise<MessageFeedback> {
  const response = await neoStreamApi.put<MessageFeedback>(
    `/neo/chat/messages/${messageId}/feedback`,
    data,
  )
  return response.data
}

export async function removeMessageFeedback(messageId: string): Promise<void> {
  await neoStreamApi.delete(`/neo/chat/messages/${messageId}/feedback`)
}
